import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import { ArrowBack, Save, Work } from '@mui/icons-material';
import PITargetRangeSelector from '../components/PITargetRangeSelector';
import PIJobReportHelper from '../components/PIJobReportHelper';
import api from '../services/api';

interface WorkflowTemplate {
  id: number;
  name: string;
  template_type: string;
  description: string;
}

const AddJob: React.FC = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [workflowTemplates, setWorkflowTemplates] = useState<WorkflowTemplate[]>([]);
  const [workflowsEnabled, setWorkflowsEnabled] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    department: '',
    description: '',
    location: 'Rogue River, OR',
    employment_type: 'full_time',
    salary_min: '',
    salary_max: '',
    positions_available: '1',
    application_deadline: '',
    workflow_template_id: '',
    // PI Target Ranges (centered at 0σ = 50, with 1σ width ≈ 16.67)
    ideal_factor_a_min: 42,
    ideal_factor_a_max: 58,
    ideal_factor_b_min: 42,
    ideal_factor_b_max: 58,
    ideal_factor_c_min: 42,
    ideal_factor_c_max: 58,
    ideal_factor_d_min: 42,
    ideal_factor_d_max: 58,
    requires_a_greater_than_b: false,
    requires_a_greater_than_c: false,
    requires_a_greater_than_d: false,
    requires_b_greater_than_a: false,
    requires_b_greater_than_c: false,
    requires_b_greater_than_d: false,
    requires_c_greater_than_a: false,
    requires_c_greater_than_b: false,
    requires_c_greater_than_d: false,
    requires_d_greater_than_a: false,
    requires_d_greater_than_b: false,
    requires_d_greater_than_c: false,
    job_summary: '',
    behavioral_description: '',
  });

  useEffect(() => {
    // Check if workflows are enabled
    const workflowsEnabled = process.env.REACT_APP_ENABLE_WORKFLOWS === 'true';
    setWorkflowsEnabled(workflowsEnabled);
    
    if (workflowsEnabled) {
      // Fetch workflow templates
      fetchWorkflowTemplates();
    }
  }, []);

  const fetchWorkflowTemplates = async () => {
    try {
      const response = await api.get('/workflows/templates');
      setWorkflowTemplates(response.data);
      // Set default template if available
      if (response.data.length > 0) {
        setFormData(prev => ({
          ...prev,
          workflow_template_id: response.data[0].id.toString()
        }));
      }
    } catch (err) {
      console.error('Failed to fetch workflow templates:', err);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/jobs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: formData.title,
          department: formData.department,
          description: formData.description,
          location: formData.location,
          employment_type: formData.employment_type,
          salary_min: formData.salary_min ? parseInt(formData.salary_min) : null,
          salary_max: formData.salary_max ? parseInt(formData.salary_max) : null,
          positions_available: parseInt(formData.positions_available),
          application_deadline: formData.application_deadline || null,
          ideal_factor_a_min: formData.ideal_factor_a_min,
          ideal_factor_a_max: formData.ideal_factor_a_max,
          ideal_factor_b_min: formData.ideal_factor_b_min,
          ideal_factor_b_max: formData.ideal_factor_b_max,
          ideal_factor_c_min: formData.ideal_factor_c_min,
          ideal_factor_c_max: formData.ideal_factor_c_max,
          ideal_factor_d_min: formData.ideal_factor_d_min,
          ideal_factor_d_max: formData.ideal_factor_d_max,
          requires_a_greater_than_b: formData.requires_a_greater_than_b,
          requires_a_greater_than_c: formData.requires_a_greater_than_c,
          requires_a_greater_than_d: formData.requires_a_greater_than_d,
          requires_b_greater_than_a: formData.requires_b_greater_than_a,
          requires_b_greater_than_c: formData.requires_b_greater_than_c,
          requires_b_greater_than_d: formData.requires_b_greater_than_d,
          requires_c_greater_than_a: formData.requires_c_greater_than_a,
          requires_c_greater_than_b: formData.requires_c_greater_than_b,
          requires_c_greater_than_d: formData.requires_c_greater_than_d,
          requires_d_greater_than_a: formData.requires_d_greater_than_a,
          requires_d_greater_than_b: formData.requires_d_greater_than_b,
          requires_d_greater_than_c: formData.requires_d_greater_than_c,
          job_summary: formData.description || formData.job_summary || null, // Use description field for job_summary
          behavioral_description: formData.behavioral_description || null,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create job');
      }

      const result = await response.json();
      
      // Create workflow if template selected
      if (workflowsEnabled && formData.workflow_template_id) {
        try {
          const jobId = result.job?.id || result.id;
          await api.post(`/workflows/jobs/${jobId}/workflow`, {
            template_id: parseInt(formData.workflow_template_id)
          });
        } catch (err) {
          console.error('Failed to create workflow:', err);
          // Don't fail the whole operation if workflow creation fails
        }
      }
      
      setSuccess('Job created successfully!');
      
      // Redirect to jobs page after 2 seconds
      setTimeout(() => {
        navigate('/jobs');
      }, 2000);
      
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/jobs')}
        sx={{ mb: 2 }}
      >
        Back to Jobs
      </Button>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Work sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">Post New Job</Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Basic Information */}
            <Typography variant="h6">Job Details</Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Job Title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
              />
              
              <TextField
                fullWidth
                label="Department"
                value={formData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
                required
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
              />

              <FormControl fullWidth>
                <InputLabel>Employment Type</InputLabel>
                <Select
                  value={formData.employment_type}
                  onChange={(e) => handleInputChange('employment_type', e.target.value)}
                  label="Employment Type"
                >
                  <MenuItem value="full_time">Full Time</MenuItem>
                  <MenuItem value="part_time">Part Time</MenuItem>
                  <MenuItem value="contract">Contract</MenuItem>
                  <MenuItem value="temporary">Temporary</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Positions Available"
                type="number"
                inputProps={{ min: 1 }}
                value={formData.positions_available}
                onChange={(e) => handleInputChange('positions_available', e.target.value)}
                required
              />
            </Box>

            <TextField
              fullWidth
              label="Job Description"
              multiline
              rows={8}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe the role, responsibilities, requirements, and what the candidate will be doing..."
            />


            {/* Salary and Deadline */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Minimum Salary"
                type="number"
                value={formData.salary_min}
                onChange={(e) => handleInputChange('salary_min', e.target.value)}
                placeholder="e.g. 50000"
              />

              <TextField
                fullWidth
                label="Maximum Salary"
                type="number"
                value={formData.salary_max}
                onChange={(e) => handleInputChange('salary_max', e.target.value)}
                placeholder="e.g. 70000"
              />

              <TextField
                fullWidth
                label="Application Deadline"
                type="date"
                value={formData.application_deadline}
                onChange={(e) => handleInputChange('application_deadline', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* Workflow Template Selection */}
            {workflowsEnabled && workflowTemplates.length > 0 && (
              <>
                <Typography variant="h6">Workflow Settings</Typography>
                <FormControl fullWidth>
                  <InputLabel>Workflow Template</InputLabel>
                  <Select
                    value={formData.workflow_template_id}
                    onChange={(e) => handleInputChange('workflow_template_id', e.target.value)}
                    label="Workflow Template"
                  >
                    <MenuItem value="">
                      <em>No Workflow</em>
                    </MenuItem>
                    {workflowTemplates.map(template => (
                      <MenuItem key={template.id} value={template.id.toString()}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {workflowTemplates.find(t => t.id.toString() === formData.workflow_template_id)?.description || 
                     'Select a workflow template to track the hiring process for this position'}
                  </FormHelperText>
                </FormControl>
              </>
            )}

            {/* PI Job Report Helper */}
            <PIJobReportHelper 
              onExtract={(data) => {
                // Fill job title if empty
                if (data.jobTitle && !formData.title) {
                  handleInputChange('title', data.jobTitle);
                }
                // Fill department if empty
                if (data.department && !formData.department) {
                  handleInputChange('department', data.department);
                }
                // Fill job summary
                if (data.jobSummary) {
                  handleInputChange('job_summary', data.jobSummary);
                }
                // Fill job characteristics as part of description
                if (data.jobCharacteristics && !formData.description) {
                  handleInputChange('description', data.jobCharacteristics);
                }
                // Fill behavioral description
                if (data.behaviors && data.behaviors.length > 0) {
                  handleInputChange('behavioral_description', data.behaviors.join(', '));
                }
                // Apply suggested PI ranges
                if (data.suggestedRanges) {
                  Object.entries(data.suggestedRanges).forEach(([key, value]) => {
                    handleInputChange(key, value);
                  });
                }
              }}
            />

            {/* PI Behavioral Target */}
            <PITargetRangeSelector 
              values={formData}
              onChange={handleInputChange}
            />

            {/* Submit Button */}
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate('/jobs')}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
                disabled={submitting}
                data-testid="save-job-button"
              >
                {submitting ? 'Creating...' : 'Post Job'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default AddJob;